/* eslint-disable react/no-access-state-in-setstate */
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import React, { Component } from 'react';
import { connect } from 'react-redux';
import { experimentIdSelector } from 'ctm-selectors';

import { sanitizeInnerHtml, applyVerticalStyles, interpolateString  } from 'ctm-util-functions';

import { Tooltip } from '../../tooltip/tooltip';
import { Anchor } from '../../anchor/anchor';

import styles from './help_indicator.css';

/**
 * HelpIndicator React Component
 * @extends React.Component
 * @desc Used to render and info button and display a tooltip with help text available from the form schema.
 * @returns {Node} a HelpIndicator Component
 */
class _HelpIndicator extends Component {

    constructor(props, context) {
        super(props, context);

        this.state = {
            tooltipTrigger: false,
            toggleInlineHelp: false
        };
        this.styles = applyVerticalStyles(styles, 'help_indicator');
        this.triggerRef = React.createRef();
    }

    /**
     * Display the helpText in a tooltip panel. Currently this just logs the help text
     * @param  {Object} event - the event.
     */
    handleClick = (event) => {
        const { tooltipTrigger } = this.state;
        const trigger = event.target;
        this.setState({
            tooltipTrigger: tooltipTrigger ? false : trigger
        });
    };

    getRef = () => {
        return this.ref;
    };

    /**
     * display the helpText in a tooltip panel. Currently this just logs the help text
     * @param  {Object} event - the event.
     */
    handleClickOutside = (event) => {
        this.setState({
            tooltipTrigger: false
        });
    };

    /**
     * Convert the content to HTML markup if format: true is passed in helpText
     * @param {String} content The tooltip content
     * @returns {Node} - The span with the content
     */
    getHtml = (content) => {
        return <span className={styles.html} dangerouslySetInnerHTML={{ __html: sanitizeInnerHtml(content) }} />;
    };

    /**
     * Get the tooltip component for the help indicator.
     * @returns {Node|null} - Tooltip component.
     */
    getToolTip = () => {
        const { tooltipTrigger } = this.state;
        const { contentProps, selectedValues, specialFlags, experimentId } = this.props;
        if (tooltipTrigger) {
            const { title, format = false, content } = contentProps;
            const { placement = 'right' } = this.props;
            const titleComponent = title ? (<span className={this.styles.tooltip_title}>{title}</span>) : null;

            const interpolatedContent = interpolateString(content)(selectedValues || {});
            const tooltipContent = format === 'html' ? this.getHtml(interpolatedContent) : interpolatedContent;
            return ReactDOM.createPortal(
                <div data-experiment={experimentId}>
                    <Tooltip
                        dataId={contentProps.label}
                        onClickOutside={this.handleClickOutside}
                        placement={placement}
                        variant="brand_base_0"
                        specialFlags={ !specialFlags ? null : specialFlags }
                        trigger={tooltipTrigger}>
                        {titleComponent}
                        {tooltipContent}
                    </Tooltip>
                </div>,
                document.body
            );
        }
        return null;
    };

    /**
     * Toggle the state flag used for the in-line help Content.
     */
    toggleInlineHelp = () => {
        const { toggleInlineHelp } = this.state;
        this.setState({ toggleInlineHelp: !toggleInlineHelp });
    };

    /**
     * Get the in-line Content for the help indicator.
     * @param {Object} contentProps The contentProps
     * @param {String} contentProps.title The short text to be shown on the helper text.
     * @param {String} contentProps.content The long text to be shown on the helper text.
     * @param {String} identifier The identifier
     * @return {Node} The in-line Content.
     */
    getInlineContent = ({ title, content }, identifier) => {
        const { toggleInlineHelp } = this.state;
        const [tail, displayText] = (!toggleInlineHelp && title) ? [' more', title] : [' less', content];
        const inlineHelpContentAnchor = (
            <Anchor
                key={`${identifier}.toggleLink`}
                className={this.styles.inline_help_content_anchor}
                label={tail}
                onClick={this.toggleInlineHelp} />
        );

        return title ? [displayText, inlineHelpContentAnchor] : [displayText];
    };

    /**
     * Return a small button used to trigger the tooltip.
     * @returns {Node} The HelpIndicator
     */
    render = () => {
        const { schema, contentProps } = this.props;
        const { tooltipTrigger } = this.state;
        const identifier = schema.identifier;
        const type = contentProps.type;
        const toolTip = this.getToolTip();
        const buttonVariant = contentProps.variant || 'form_tooltip';
        const buttonVariantStyle = tooltipTrigger ? `${buttonVariant}_active` : buttonVariant;
        const elementId = `${identifier}.helpIcon`;
        if (type === 'inline') {
            return (
                <div className={this.styles.inline_help_content}>
                    {this.getInlineContent(contentProps, identifier)}
                </div>
            );
        }

        return (
            <>
                {toolTip}
                <button
                    type="button"
                    key={elementId}
                    id={elementId}
                    ref={this.triggerRef}
                    data-id={elementId}
                    className={this.styles[`variant_${buttonVariantStyle}`]}
                    onClick={this.handleClick}
                >
                    ?
                </button>
            </>
        );
    };

}

_HelpIndicator.propTypes = {
    /**
     * Content props to be passed through.
     */
    contentProps: PropTypes.shape({
        /**
         * Title of the help indicator.
         */
        title: PropTypes.string,
        /**
         * Content for the help indicator.
         */
        content: PropTypes.string,
        /**
         * Pass format: "html" to return html or leave it off to return a string
         */
        format: PropTypes.string,
        /**
         * Type of the help indicator. tooltip is used if not set
         */
        type: PropTypes.oneOf(['tooltip', 'inline']),
        /**
         * Style variant specified in the schema, default to 'form_tooltip'
         */
        variant: PropTypes.string,
        label: PropTypes.string
    }).isRequired,
    placement: PropTypes.string,
    schema: PropTypes.object.isRequired,
    selectedValues: PropTypes.object,
    specialFlags: PropTypes.object
};

const mapStateToProps = (state, props) => {
    return { selectedValues: props.selectedValues, experimentId: experimentIdSelector(state) };
};

const HelpIndicator = connect(mapStateToProps)(_HelpIndicator);

export { _HelpIndicator, HelpIndicator };
